import LegalPage from "./LegalPage";

const H = ({ children }) => (
  <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-8 mb-3">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[var(--pa-text-muted)] leading-relaxed">{children}</p>
);
const L = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 text-[var(--pa-text-muted)]">{children}</ul>
);

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      kicker="Legal · Terms"
      testId="terms-page"
    >
      <P>
        These Terms of Service (the "Terms") govern your access to and use of
        the ProsthoAI clinical decision-support platform (the "Service"). By
        creating an account you agree to these Terms together with the
        accompanying Business Associate Agreement.
      </P>

      <H>1. Eligibility</H>
      <P>
        The Service is intended for use by licensed dental and medical
        professionals only. You represent that you hold an active, unrestricted
        professional license in your jurisdiction and that you will use the
        Service exclusively in the course of your professional practice.
      </P>

      <H>2. Clinical Decision Support — Not a Diagnosis</H>
      <P>
        ProsthoAI is a decision-support tool. The Service surfaces
        AI-generated clinical reasoning, treatment-planning pathways, and
        specialist-style analyses. All clinical decisions remain the sole
        professional responsibility of the treating clinician. ProsthoAI is
        not a medical device and is not intended to diagnose, treat, cure, or
        prevent any disease.
      </P>

      <H>3. Account Security</H>
      <L>
        <li>You are responsible for the confidentiality of your credentials.</li>
        <li>You must notify ProsthoAI immediately of any unauthorized use.</li>
        <li>
          Sharing accounts is prohibited; each clinician requires an
          individual seat.
        </li>
      </L>

      <H>4. Subscription, Billing, and Quotas</H>
      <L>
        <li>
          The Service is offered on a monthly subscription basis through Stripe
          (Starter: $99/month — 50 AI analyses; Practice: $249/month — unlimited).
        </li>
        <li>
          AI analyses include both module consultations and Complete Case
          Analyses combined, counted from the first day of each calendar month
          (UTC).
        </li>
        <li>
          Cancellations take effect at the end of the current billing period.
        </li>
      </L>

      <H>5. Acceptable Use</H>
      <P>You agree NOT to:</P>
      <L>
        <li>
          Upload PHI for patients who have not consented to digital handling
          where consent is required by local law.
        </li>
        <li>
          Use the Service for non-clinical, research, or product-development
          purposes without a separate written agreement.
        </li>
        <li>
          Reverse-engineer, scrape, or attempt to extract the underlying AI
          model behavior.
        </li>
        <li>Share AI output as a substitute for licensed professional advice.</li>
      </L>

      <H>6. Data Handling and Privacy</H>
      <P>
        Handling of Protected Health Information is governed by the
        Business Associate Agreement you accepted at signup. ProsthoAI logs
        every PHI-touching event for compliance auditing. Files uploaded
        through the imaging portal are stored encrypted at rest and isolated
        per authenticated clinician account.
      </P>

      <H>7. Intellectual Property</H>
      <P>
        ProsthoAI and the underlying methodology (US Patent Pending
        Application 64/062,147) are the exclusive property of Dr. Ronik
        Seecharan DMD and ProsthoAI. You retain ownership of any clinical
        data you upload; ProsthoAI receives a limited license to process that
        data solely for the purpose of providing the Service to you.
      </P>

      <H>8. Limitation of Liability</H>
      <P>
        To the maximum extent permitted by law, ProsthoAI shall not be liable
        for any indirect, incidental, special, consequential, or punitive
        damages arising out of your use of the Service. ProsthoAI's aggregate
        liability shall not exceed the fees paid by you in the twelve months
        preceding the event giving rise to the claim.
      </P>

      <H>9. Termination</H>
      <P>
        ProsthoAI may suspend or terminate your access for material breach of
        these Terms or the BAA, for failure to pay, or where required by law.
        You may terminate your account at any time from the Account page.
      </P>

      <H>10. Changes to the Terms</H>
      <P>
        ProsthoAI may revise these Terms or the BAA from time to time. Where a
        change is material, we will require you to re-accept the updated
        document before continuing to use the Service. A version identifier
        and timestamp are recorded on every acceptance.
      </P>

      <H>11. Contact</H>
      <P>
        Questions about these Terms or the BAA: contact ProsthoAI at the
        address listed on your account invoice or via the Account page.
      </P>

      <P className="mt-8 text-xs text-[var(--pa-text-dim)]">
        Patent Pending · US Application 64/062,147. Designed by Dr. Ronik
        Seecharan DMD, Prosthodontist, Boca Raton, Florida.
      </P>
    </LegalPage>
  );
}
